import React from 'react';
import styled from 'styled-components';
import MuiPagination from '@mui/material/Pagination';
import PaginationItem, {
  PaginationItemProps,
} from '@mui/material/PaginationItem';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import TableTheme from '@grnhse/seedling/lib/birch/components/table/theme';
import ArrowLeftIcon from '@grnhse/seedling/lib/birch/icons/arrow_left';
import ArrowRightIcon from '@grnhse/seedling/lib/birch/icons/arrow_right';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { withLocalePrefix } from 'shared/utils/translation';
import PageSizeSelector from 'talent_matching/components/table/page_size_selector';

const t = withLocalePrefix('talent_matching');

const StyledPaginationItem = styled(PaginationItem)`
  font-family: ${TableTheme.default.font};
  color: ${TableTheme.default.textColor};
  font-size: 1rem;
  min-width: ${Tokens.spacing[5]};
  height: ${Tokens.spacing[5]};
  margin: 0;

  &:hover {
    background-color: ${TableTheme.default.paginationItemHoverBackgroundColor};
  }

  &.Mui-selected {
    background-color: ${TableTheme.default.paginationItemBackgroundColor};
    font-weight: 500;

    &:hover {
      background-color: ${TableTheme.default.paginationItemBackgroundColor};
    }
  }

  &.Mui-disabled {
    opacity: 1;
  }

  &.MuiPaginationItem-ellipsis {
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: transparent;
    }
  }
`;

type PaginationButtonProps = PaginationItemProps & {
  disableRipple?: boolean;
};

const PaginationButton = (props: PaginationButtonProps) => (
  <StyledPaginationItem {...props} />
);

type Props = {
  currentPage: number;
  totalRows: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
};

const TablePaginationControls = ({
  currentPage,
  totalRows,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: Props) => {
  const pageCount = Math.max(Math.ceil(totalRows / rowsPerPage), 1);

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      flexWrap="wrap"
      justifyContent="flex-end"
    >
      {onRowsPerPageChange ? (
        <PageSizeSelector
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      ) : (
        <SecondaryBody metadata>
          {t('table.footer.rows_per_page', { count: rowsPerPage })}
        </SecondaryBody>
      )}
      <MuiPagination
        shape="rounded"
        count={pageCount}
        page={Math.min(currentPage, pageCount)}
        onChange={(_event, value) => onPageChange(value - 1)}
        renderItem={(item) => (
          <PaginationButton
            disableRipple
            slots={{
              previous: () => (
                <ArrowLeftIcon
                  size="xs"
                  color={
                    item.disabled
                      ? TableTheme.default.disabledPaginationItemColor
                      : TableTheme.default.activePaginationItemColor
                  }
                />
              ),
              next: () => (
                <ArrowRightIcon
                  size="xs"
                  color={
                    item.disabled
                      ? TableTheme.default.disabledPaginationItemColor
                      : TableTheme.default.activePaginationItemColor
                  }
                />
              ),
            }}
            {...item}
          />
        )}
      />
    </Stack>
  );
};

export default TablePaginationControls;
